import { ref, h } from 'vue'
import { defineStore } from 'pinia'
import { TYPE, useToast } from 'vue-toastification'
import i18n from '@/addons/i18n'
import { FeLogger } from '@/addons/monitoring'
import modalController, { ModalTypes } from '@/components/modal/ModalController'
import MobileToastWrapper from '@/abs-core/shared/components/core/MobileToastWrapper.vue'

export const useToastsStore = defineStore('ToastsStore', () => {
  const toast = useToast()

  /**
   * Check if the pattern is i18n key-like then translate it, otherwise it just
   * return the string
   *
   * @param textOrLabelId
   */
  function getLabel(textOrLabelId: string) {
    const matches = /\w+\.\w+/.test(textOrLabelId)
    if (matches) {
      return i18n.global.t(textOrLabelId)
    }

    return textOrLabelId
  }

  /**
   *
   * @param messageKey
   * @param type
   * @param timeout
   */
  const addToast = (
    content: string | any,
    type: TYPE = TYPE.DEFAULT,
    timeout?: number,
  ) => {
    const isVNodeContent =
      typeof content !== 'string' &&
      content !== null &&
      typeof content === 'object' &&
      'type' in content

    return toast(
      h(MobileToastWrapper, {
        content: isVNodeContent
          ? content
          : typeof content === 'string'
            ? getLabel(content)
            : content,
        type,
      }),
      {
        hideProgressBar: true,
        toastClassName: 'fe-notification',
        icon: '',
        type: type,
        timeout: timeout,
      },
    )
  }

  /**
   * Error variant with logger included in case of unhandled exceptions
   * @param messageKey
   */
  const addToastError = (messageKey?: string) => {
    if (!messageKey) {
      FeLogger.error('Server Error - EMPTY')
      return
    }
    if (messageKey.includes('Unhandled exception')) {
      FeLogger.error('Server Error - UNHANDLED EXCEPTION ' + messageKey)
      messageKey = i18n.global.t('pos_common.unhandled_error')
    }

    return addToast(messageKey || '', TYPE.ERROR)
  }

  /**
   * Shorthand
   * @param messageKey
   */
  const addToastSuccess = (messageKey: string) =>
    addToast(messageKey, TYPE.SUCCESS)

  /**
   * Shorthand
   * @param messageKey
   */
  const addToastInfo = (messageKey: string) => addToast(messageKey, TYPE.INFO)

  /**
   * Shorthand
   * @param messageKey
   */
  const addToastWarn = (messageKey: string) =>
    addToast(messageKey, TYPE.WARNING)

  /**
   *
   */
  const wrongSignAlertOpen = ref(false)

  /**
   *
   * @param title
   * @param message
   */
  const addToastWrongSign = (title?: string, message?: string) => {
    wrongSignAlertOpen.value = true

    modalController.open({
      type: ModalTypes.INFO,
      component: '',
      title: title ?? i18n.global.t('pos_common.attention'),
      message: message ?? i18n.global.t('pos_sale.same_sign_payment'),
      confirmActionButton: false,
      closedAction: () => {
        wrongSignAlertOpen.value = false
      },
    })
  }

  return {
    getLabel,
    addToast,
    addToastError,
    addToastSuccess,
    addToastInfo,
    addToastWarn,
    wrongSignAlertOpen,
    addToastWrongSign,
  }
})
